import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Stack, Typography } from '@mui/material';
import { addDays, format, isSameDay, startOfWeek } from 'date-fns';
import { dayKey } from './calendarEvents';
import OrderEventChip from './OrderEventChip';

/**
 * Seven day columns; each lists that day's bookings in start-time order. A multi-day booking appears
 * in every column it occupies. There is no hour grid: a booking is whole provider-local days
 * (contract §10), nothing in the data model has an hourly duration.
 * Props: date (any day of the week), buckets, notesSummary, availabilityDates, locale, now, onOpenOrder.
 */
export default function WeekView({ date, buckets, notesSummary, availabilityDates = [], locale, now, onOpenOrder }) {
  const { t } = useTranslation();
  const weekStart = startOfWeek(date, { locale });
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const available = new Set(availabilityDates);

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(140px, 1fr))', gap: 0.5, minWidth: 980 }}>
        {days.map((day) => {
          const key = dayKey(day);
          const events = buckets[key] || [];
          const isToday = isSameDay(day, now);
          const isAvailable = available.has(key);
          return (
            <Box
              key={key} data-testid={`week-column-${key}`} data-available={isAvailable ? 'true' : 'false'}
              sx={{
                minHeight: 320, p: 0.75, border: 1, borderRadius: 1, borderColor: isToday ? 'primary.main' : 'divider',
                bgcolor: isAvailable ? 'background.paper' : 'action.disabledBackground',
              }}
            >
              <Typography
                variant="caption" align="center" display="block"
                sx={{ fontWeight: isToday ? 700 : 500, color: isToday ? 'primary.main' : 'text.secondary', mb: 0.5 }}
              >
                {format(day, 'EEE d', { locale })}
              </Typography>
              {events.length === 0 ? (
                <Typography variant="caption" color="text.disabled" display="block" align="center">{t('orders.calendar.noEvents')}</Typography>
              ) : (
                <Stack spacing={0.5}>
                  {events.map((ev) => (
                    <OrderEventChip key={ev.id} event={ev} note={notesSummary[ev.id]} compact onClick={() => onOpenOrder(ev.order)} />
                  ))}
                </Stack>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
